import { Flex, Text, MantineSize } from '@mantine/core';
import { useCallback, useState } from 'react';
import FullScreenDrawer, { FullScreenDrawerProps } from 'src/UI/common/FullScreenDrawer';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import Step from './Step';

type DrawerFlowProps<T> = {
  opened: boolean;
  close: () => void;
  title: string;
  steps: DrawerFlowStep<T>[];
  initialData: T;
  data: T;
  setData: (data: T) => void;
  onSuccess?: (data: T) => void;
  drawerProps?: Omit<FullScreenDrawerProps, 'opened' | 'close' | 'children'>;
  flowId?: string;
};

type ProgressProps = {
  isSubmitting?: boolean;
};

export type StepProps<T> = {
  close: () => void;
  data: T;
  setData: (data: T) => void;
  clearData: () => void;
  canGoForward: boolean;
  nextStep: (data?: T) => void;
  canGoBack: boolean;
  previousStep: (data?: T) => void;
  renderProgress?: (props: ProgressProps) => React.ReactNode;
  showClientName?: boolean;
  clientNamePrefix?: string;
  onSuccess: () => void;
  allowVerticalOverflow?: boolean;
  maxContainerWidth?: MantineSize;
};

export type DrawerFlowStep<T> = {
  title: string;
  showClientName?: boolean;
  clientNamePrefix?: string;
  onNext?: (data: T, flowId?: string) => void;
  screens: {
    component: React.ComponentType<StepProps<T>>;
    allowVerticalOverflow?: boolean;
    renderProgress?: (props: ProgressProps) => React.ReactNode;
    onNext?: (data: T, flowId?: string) => void;
    maxContainerWidth?: MantineSize;
  }[];
};

export default function DrawerFlow<T>({
  opened,
  close,
  title,
  steps,
  initialData,
  onSuccess,
  data,
  setData,
  drawerProps,
  flowId,
}: DrawerFlowProps<T>) {
  const [currentStep, setCurrentStep] = useState(0);
  const [currentScreen, setCurrentScreen] = useState(0);
  const step = steps[currentStep];
  const screenData = step.screens[currentScreen];
  const Screen = screenData.component;
  const clearData = useCallback(() => {
    setData(initialData);
  }, [initialData]);

  const handleClose = useCallback(() => {
    clearData();
    setCurrentStep(0);
    setCurrentScreen(0);
    close();
  }, [clearData, close]);

  const handleNextStepFn = (newData?: T) => {
    if (screenData.onNext) {
      screenData.onNext(data, flowId);
    } else if (step.onNext) {
      step.onNext(data, flowId);
    }

    handleNextStep(newData);
  };

  const handleNextStep = useCallback(
    (newData?: T) => {
      if (newData !== undefined) {
        setData(newData);
      }

      if (step.screens.length > currentScreen + 1) {
        setCurrentScreen(currentScreen + 1);
      } else {
        setCurrentStep(currentStep + 1);
        setCurrentScreen(0);
      }
    },
    [currentScreen, currentStep, step.screens.length],
  );

  const handlePreviousStep = useCallback(
    (data?: T) => {
      if (data !== undefined) {
        setData(data);
      }

      if (currentScreen > 0) {
        setCurrentScreen(currentScreen - 1);
      } else if (currentStep > 0) {
        const previousStep = steps[currentStep - 1];
        setCurrentStep(currentStep - 1);
        setCurrentScreen(previousStep.screens.length - 1);
      }
    },
    [currentScreen, currentStep, steps],
  );

  const handleSuccess = useCallback(() => {
    if (onSuccess) {
      onSuccess(data);
    }
    handleClose();
  }, [data, onSuccess, handleClose]);

  const canGoBack = currentStep > 0 || currentScreen > 0;
  const canGoForward = currentScreen < step.screens.length - 1 || currentStep < steps.length - 1;

  return (
    <FullScreenDrawer opened={opened} close={handleClose} {...drawerProps}>
      <Flex
        direction="column"
        h={screenData.allowVerticalOverflow ? 'auto' : '100%'}
        pb={60}
        mih={0}
      >
        <Text mb={steps.length > 1 ? 'md' : undefined} fw={FONT_WEIGHTS.medium} size="textMd">
          {title}
        </Text>
        {steps.length > 1 && (
          <Flex gap="xs" w="100%" mb="xxs">
            {steps.map((step, i) => (
              <Step
                key={i}
                title={step.title}
                stepNumber={i}
                currentStep={currentStep}
                numScreens={step.screens.length}
                currentScreen={currentScreen}
              />
            ))}
          </Flex>
        )}
        <Flex mt="xl" w="100%" h={screenData.allowVerticalOverflow ? 'auto' : '100%'} mih={450}>
          <Screen
            close={handleClose}
            data={data}
            setData={setData}
            clearData={clearData}
            canGoForward={canGoForward}
            nextStep={handleNextStepFn}
            canGoBack={canGoBack}
            previousStep={handlePreviousStep}
            renderProgress={screenData.renderProgress}
            showClientName={step.showClientName}
            clientNamePrefix={step.clientNamePrefix}
            onSuccess={handleSuccess}
            allowVerticalOverflow={screenData.allowVerticalOverflow}
            maxContainerWidth={screenData.maxContainerWidth}
          />
        </Flex>
      </Flex>
    </FullScreenDrawer>
  );
}
